import { useCallback, useMemo, useEffect } from 'react';
import { Navigate, useParams, Outlet } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import LayoutWrapper from '../components/layout/LayoutWrapper';
import useAutoformMetadata from '../components/autoform/hooks/useAutoformMetadata';
import SidebarItemListContainer from '../components/autoform/SidebarItemListContainer';
import LoadingModal from '../components/modal/LoadingModal';
import { Title } from 'react-head';
import useAutoformLoadMetadata from '../components/autoform/hooks/useAutoformLoadMetadata';
import useHistoryPush from '../hooks/useHistoryPush';
import Autoform from 'components/autoform/Autoform';
import { APP_NAME as appName } from 'constants/autoforms';
import _ from 'lodash';
import { onHasUnsavedChanges, setSearchTerm } from 'store/helpers/helpersSlice';

const defaultConfig = {
    addSubItemMessageId: 'autoform_add_new_subitem',
};

const AutoformPage = ({ hasSecondaryMenu, config: customConfig = {} }) => {
    const config = useMemo(() => _.merge({}, defaultConfig, customConfig), [customConfig]);
    const intl = useIntl();
    const isSidebarVisible = useSelector((state) => state.helpers.isSidebarVisible);
    const { metadata, hierarchicalTables } = useAutoformMetadata();
    const dispatch = useDispatch();
    const { autoformId } = useParams();
    const historyPush = useHistoryPush();
    const schemaCode = config.autoformId ?? autoformId;
    const { isLoading, loadingError } = useAutoformLoadMetadata(schemaCode);
    const level1TableId = hierarchicalTables.level1[0]?.id;
    const showAsGridHint = hierarchicalTables.level1[0]?.showAsGridHint;

    const showSideBar = useMemo(() => {
        return isSidebarVisible && (!showAsGridHint || metadata?.maxTableHierarchyLevel > 1);
    }, [isSidebarVisible, showAsGridHint, metadata?.maxTableHierarchyLevel]);

    const onItemDelete = useCallback(() => {
        dispatch(onHasUnsavedChanges(false));
        historyPush({
            pathname: '.',
            search: new URLSearchParams(window.location.search),
        });
    }, [historyPush, dispatch]);

    useEffect(() => {
        return () => {
            dispatch(setSearchTerm(''));
        };
    }, [dispatch, schemaCode]);

    if (!metadata || isLoading) {
        return <LoadingModal header={intl.formatMessage({ id: 'autoform_loading_modal_message' })} isOpen />;
    }

    if (loadingError) {
        return <Navigate to={'/'} replace />;
    }

    return (
        <>
            <Title>{config?.pageTitle ?? `${metadata.formLabel} - ${appName}`}</Title>
            <LayoutWrapper
                hasSecondaryMenu={hasSecondaryMenu}
                sidebar={showSideBar && <SidebarItemListContainer tableId={level1TableId} config={config} />}
                content={
                    !showAsGridHint || metadata.maxTableHierarchyLevel > 1 ? (
                        <Outlet context={{ onNodeItemDelete: onItemDelete, config }} />
                    ) : (
                        <Autoform onNodeItemDelete={onItemDelete} config={config} />
                    )
                }
            />
        </>
    );
};

export default AutoformPage;
